import React, { useState, useEffect } from 'react';
import { UsersIcon, AcademicCapIcon, ClockIcon } from '@heroicons/react/24/outline';
import Layout from '../../components/Layout';
import { CefrBadge } from '../../components/Badge';
import api from '../../utils/api';

const C = { teal:'#00B4D8', success:'#10B981', warning:'#F59E0B', dark:'#1F2937', grey:'#6B7280', border:'#E5E7EB', bg:'#F9FAFB', navy:'#0D1B2A' };

export default function TutorStudents() {
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    api.get('/api/tutor/students').then(r => {
      setStudents(r.data.students || []);
    }).catch(() => {}).finally(() => setLoading(false));
  }, []);

  const filtered = students.filter(s => {
    const matchSearch = !search || (s.full_name || '').toLowerCase().includes(search.toLowerCase()) ||
      (s.email || '').toLowerCase().includes(search.toLowerCase());
    const matchFilter = filter === 'all' || s.cefr_level === filter;
    return matchSearch && matchFilter;
  });

  const levels = [...new Set(students.map(s => s.cefr_level).filter(Boolean))].sort();

  const formatDate = (iso) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleDateString('en-GB', { day:'numeric', month:'short', year:'numeric' });
  };

  return (
    <Layout role="tutor">
      {/* Stats */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:20, marginBottom:24 }}>
        {[
          { label:'Total Students', value: students.length, icon: UsersIcon, color: C.teal },
          { label:'Avg Level', value: students.length > 0 ? (students.map(s => s.cefr_level||'A1').sort().slice(Math.floor(students.length/2))[0] || '—') : '—', icon: AcademicCapIcon, color: '#8B5CF6' },
          { label:'Total Lessons', value: students.reduce((a,s) => a + (s.lesson_count||0), 0), icon: ClockIcon, color: C.success },
        ].map(({ label, value, icon: Icon, color }) => (
          <div key={label} className="card" style={{ padding:20, display:'flex', alignItems:'center', gap:16 }}>
            <div style={{ width:44, height:44, borderRadius:12, background:`${color}20`, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
              <Icon style={{ width:22, height:22, color }} />
            </div>
            <div>
              <div style={{ fontSize:11, fontWeight:600, color:C.grey, textTransform:'uppercase', letterSpacing:'0.5px', marginBottom:2 }}>{label}</div>
              <div style={{ fontSize:26, fontWeight:800, color:C.dark }}>{value}</div>
            </div>
          </div>
        ))}
      </div>

      <div className="card" style={{ overflow:'hidden' }}>
        {/* Header + filters */}
        <div style={{ padding:'20px 24px', borderBottom:`1px solid ${C.border}`, display:'flex', justifyContent:'space-between', alignItems:'center', flexWrap:'wrap', gap:12 }}>
          <h3 style={{ margin:0, fontSize:15, fontWeight:700 }}>My Students</h3>
          <div style={{ display:'flex', gap:10, alignItems:'center' }}>
            <input
              value={search} onChange={e => setSearch(e.target.value)}
              placeholder="Search students..."
              style={{ padding:'8px 14px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, outline:'none', minWidth:200 }}
            />
            <select value={filter} onChange={e => setFilter(e.target.value)}
              style={{ padding:'8px 12px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13 }}>
              <option value="all">All Levels</option>
              {levels.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </div>
        </div>

        {loading ? (
          <div style={{ textAlign:'center', padding:60, color:C.grey }}>Loading students...</div>
        ) : filtered.length === 0 ? (
          <div style={{ textAlign:'center', padding:60 }}>
            <UsersIcon style={{ width:48, height:48, color:C.border, margin:'0 auto 16px', display:'block' }} />
            <h4 style={{ margin:'0 0 8px', color:C.dark }}>
              {students.length === 0 ? 'No students yet' : 'No students match your search'}
            </h4>
            <p style={{ color:C.grey, fontSize:14, margin:0 }}>
              {students.length === 0
                ? 'Students assigned to your lessons will appear here.'
                : 'Try adjusting your search or filter.'}
            </p>
          </div>
        ) : (
          <div style={{ overflowX:'auto' }}>
            <table style={{ width:'100%', borderCollapse:'collapse' }}>
              <thead>
                <tr style={{ background:C.bg }}>
                  {['Student', 'Level', 'Lessons', 'Last Lesson', 'Status'].map(h => (
                    <th key={h} style={{ padding:'12px 16px', textAlign:'left', fontSize:12, fontWeight:600, color:C.grey, textTransform:'uppercase', letterSpacing:'0.5px' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {filtered.map((s, i) => (
                  <tr key={s.id || i} style={{ borderTop:`1px solid ${C.border}` }}>
                    <td style={{ padding:'14px 16px' }}>
                      <div style={{ display:'flex', alignItems:'center', gap:10 }}>
                        <div style={{ width:36, height:36, borderRadius:'50%', background:`${C.teal}20`, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:C.teal, fontSize:14, flexShrink:0 }}>
                          {(s.full_name || s.email || 'S')[0].toUpperCase()}
                        </div>
                        <div>
                          <div style={{ fontWeight:600, fontSize:14, color:C.dark }}>{s.full_name || '—'}</div>
                          <div style={{ fontSize:12, color:C.grey }}>{s.email || ''}</div>
                        </div>
                      </div>
                    </td>
                    <td style={{ padding:'14px 16px' }}>
                      <CefrBadge level={s.cefr_level || 'A1'} />
                    </td>
                    <td style={{ padding:'14px 16px', fontWeight:600, color:C.dark }}>{s.lesson_count || 0}</td>
                    <td style={{ padding:'14px 16px', color:C.grey, fontSize:13 }}>{formatDate(s.last_lesson)}</td>
                    <td style={{ padding:'14px 16px' }}>
                      <span style={{ fontSize:12, fontWeight:600, padding:'4px 10px', borderRadius:20, background:'#D1FAE5', color:'#065F46' }}>Active</span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Layout>
  );
}
